import type {
  DashboardStats,
  FaqCategory,
  FaqItem,
  FaqListResponse,
  FaqListParams,
  CreateFaqRequest,
  SessionListParams,
  SessionListResponse,
  SessionDetailResponse,
  ChatMessagesResponse,
  ChatMessagesUpdateRequest,
  SymptomListParams,
  SymptomListResponse,
  SymptomDetail,
  CreateSymptomRequest,
  UpdateSymptomRequest,
  BulkUploadRequest,
  BulkUploadResponse,
  UserListResponse,
  UserItem,
  AllSymptomsResponse,
} from '../types';
import { mockService } from './mock';

// API Gateway REST API は %2F をパスセパレータとして解釈するため、
// スラッシュを含むパスパラメータは二重エンコードが必要
function encodePathParam(value: string): string {
  return encodeURIComponent(value).replace(/%2F/gi, '%252F');
}

export interface ApiService {
  getDashboardStats(yearMonth?: string): Promise<DashboardStats>;
  getSessions(params: SessionListParams): Promise<SessionListResponse>;
  getSessionDetail(id: string): Promise<SessionDetailResponse>;
  exportSessionsCsv(yearMonth: string): Promise<Blob>;
  getCategories(): Promise<FaqCategory[]>;
  createCategory(
    data: Pick<FaqCategory, 'id' | 'name' | 'productCategoryId'>,
  ): Promise<FaqCategory>;
  deleteCategory(id: string): Promise<void>;
  updateCategory(id: string, data: Pick<FaqCategory, 'name' | 'sortOrder'>): Promise<FaqCategory>;
  getFaqs(params: FaqListParams): Promise<FaqListResponse>;
  getAllFaqs(): Promise<FaqItem[]>;
  getFaqById(id: string): Promise<FaqItem | null>;
  createFaq(data: CreateFaqRequest): Promise<FaqItem>;
  updateFaq(id: string, data: Partial<CreateFaqRequest>): Promise<FaqItem>;
  deleteFaq(id: string): Promise<void>;
  reorderCategories(items: { id: string; sortOrder: number }[]): Promise<void>;
  reorderFaqs(items: { id: string; sortOrder: number }[]): Promise<void>;
  getChatMessages(): Promise<ChatMessagesResponse>;
  updateChatMessages(
    data: ChatMessagesUpdateRequest,
  ): Promise<{ message: string; updatedAt: string; updatedBy: string }>;
  getSymptoms(params: SymptomListParams): Promise<SymptomListResponse>;
  getSymptomDetail(categoryId: string, symptomName: string): Promise<SymptomDetail>;
  createSymptom(data: CreateSymptomRequest): Promise<SymptomDetail>;
  updateSymptom(
    categoryId: string,
    symptomName: string,
    data: UpdateSymptomRequest,
  ): Promise<SymptomDetail>;
  deleteSymptom(categoryId: string, symptomName: string): Promise<void>;
  getAllSymptoms(): Promise<AllSymptomsResponse>;
  bulkUpload(data: BulkUploadRequest): Promise<BulkUploadResponse>;
  getUsers(): Promise<UserListResponse>;
  createUser(email: string): Promise<UserItem>;
  deleteUser(email: string): Promise<void>;
  getIconUploadUrl(data: {
    type: 'avatar' | 'header';
    contentType: string;
  }): Promise<{ uploadUrl: string; fields: Record<string, string>; iconUrl: string }>;
}

export function createRealApiService(getIdToken: () => Promise<string | null>): ApiService {
  const adminUrl = import.meta.env.VITE_ADMIN_API_URL;

  async function fetchWithAuth(url: string, options?: RequestInit): Promise<Response> {
    const token = await getIdToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const res = await fetch(url, { ...options, headers });
    if (!res.ok) {
      throw new Error(`API error: ${res.status}`);
    }
    return res;
  }

  return {
    async getDashboardStats(yearMonth?: string) {
      const query = new URLSearchParams();
      if (yearMonth) query.set('yearMonth', yearMonth);
      const qs = query.toString();
      const res = await fetchWithAuth(`${adminUrl}/stats${qs ? `?${qs}` : ''}`);
      return res.json();
    },
    async getSessions(params) {
      const query = new URLSearchParams();
      if (params.yearMonth) query.set('yearMonth', params.yearMonth);
      if (params.limit) query.set('limit', String(params.limit));
      if (params.lastKey) query.set('lastKey', params.lastKey);
      const res = await fetchWithAuth(`${adminUrl}/sessions?${query}`);
      return res.json();
    },
    async getSessionDetail(id) {
      const res = await fetchWithAuth(`${adminUrl}/sessions/${encodeURIComponent(id)}`);
      return res.json();
    },
    async exportSessionsCsv(yearMonth) {
      const query = new URLSearchParams();
      query.set('yearMonth', yearMonth);
      const res = await fetchWithAuth(`${adminUrl}/sessions/export?${query}`);
      return res.blob();
    },
    async getCategories() {
      const res = await fetchWithAuth(`${adminUrl}/faq-categories`);
      return res.json();
    },
    async createCategory(data) {
      const res = await fetchWithAuth(`${adminUrl}/faq-categories`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.json();
    },
    async deleteCategory(id) {
      await fetchWithAuth(`${adminUrl}/faq-categories/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
    },
    async updateCategory(id, data) {
      const res = await fetchWithAuth(`${adminUrl}/faq-categories/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.json();
    },
    async getFaqs(params) {
      const query = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
      });
      if (params.faqCategoryId) query.set('faqCategoryId', params.faqCategoryId);
      if (params.keyword) query.set('keyword', params.keyword);
      const res = await fetchWithAuth(`${adminUrl}/faqs?${query}`);
      return res.json();
    },
    async getAllFaqs() {
      const all: FaqItem[] = [];
      let page = 1;
      const limit = 100;
      const MAX_PAGES = 100;
      while (page <= MAX_PAGES) {
        const res = await this.getFaqs({ page, limit });
        all.push(...res.faqs);
        if (all.length >= res.total || res.faqs.length === 0) break;
        page++;
      }
      if (page > MAX_PAGES) {
        console.warn(
          `getAllFaqs: MAX_PAGES(${MAX_PAGES}) に到達。全${all.length}件中一部が未取得の可能性があります`,
        );
      }
      return all;
    },
    async getFaqById(id) {
      const res = await fetchWithAuth(`${adminUrl}/faqs/${encodeURIComponent(id)}`);
      return res.json();
    },
    async createFaq(data) {
      const res = await fetchWithAuth(`${adminUrl}/faqs`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.json();
    },
    async updateFaq(id, data) {
      const res = await fetchWithAuth(`${adminUrl}/faqs/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.json();
    },
    async deleteFaq(id) {
      await fetchWithAuth(`${adminUrl}/faqs/${encodeURIComponent(id)}`, {
        method: 'DELETE',
      });
    },
    async reorderCategories(items) {
      await fetchWithAuth(`${adminUrl}/faq-categories/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ items }),
      });
    },
    async reorderFaqs(items) {
      await fetchWithAuth(`${adminUrl}/faqs/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ items }),
      });
    },
    async getChatMessages() {
      const res = await fetchWithAuth(`${adminUrl}/chat-messages`);
      return res.json();
    },
    async updateChatMessages(data) {
      const res = await fetchWithAuth(`${adminUrl}/chat-messages`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return res.json();
    },
    async getSymptoms(params) {
      const query = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
      });
      if (params.categoryId) query.set('categoryId', params.categoryId);
      if (params.keyword) query.set('keyword', params.keyword);
      const res = await fetchWithAuth(`${adminUrl}/master/symptoms?${query}`);
      return res.json();
    },
    async getSymptomDetail(categoryId, symptomName) {
      const res = await fetchWithAuth(
        `${adminUrl}/master/symptoms/${encodeURIComponent(categoryId)}/${encodePathParam(symptomName)}`,
      );
      return res.json();
    },
    async createSymptom(data) {
      const res = await fetchWithAuth(`${adminUrl}/master/symptoms`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.json();
    },
    async updateSymptom(categoryId, symptomName, data) {
      const res = await fetchWithAuth(
        `${adminUrl}/master/symptoms/${encodeURIComponent(categoryId)}/${encodePathParam(symptomName)}`,
        { method: 'PUT', body: JSON.stringify(data) },
      );
      return res.json();
    },
    async deleteSymptom(categoryId, symptomName) {
      await fetchWithAuth(
        `${adminUrl}/master/symptoms/${encodeURIComponent(categoryId)}/${encodePathParam(symptomName)}`,
        { method: 'DELETE' },
      );
    },
    async getAllSymptoms() {
      const res = await fetchWithAuth(`${adminUrl}/master/symptoms/all`);
      return res.json();
    },
    async bulkUpload(data) {
      const res = await fetchWithAuth(`${adminUrl}/bulk-upload`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.json();
    },
    async getUsers() {
      const res = await fetchWithAuth(`${adminUrl}/users`);
      return res.json();
    },
    async createUser(email: string) {
      const res = await fetchWithAuth(`${adminUrl}/users`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return res.json();
    },
    async deleteUser(email: string) {
      await fetchWithAuth(`${adminUrl}/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
    },
    async getIconUploadUrl(data) {
      const res = await fetchWithAuth(`${adminUrl}/icons/upload-url`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return res.json();
    },
  };
}

export function getApiService(mode?: string): ApiService {
  const apiMode = mode ?? import.meta.env.VITE_API_MODE ?? 'mock';

  if (apiMode === 'mock') {
    return mockService;
  }

  return createRealApiService(async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  });
}

// シングルトンとしてエクスポート
export const apiService = getApiService();
